import { FareFamilyPriceFragment, ResultsLegFragment, AviaSearchParameters } from '@websky/graphql';
import {
	DataLayerEvent,
	DataLayerItemsListName,
	ItemCategory,
	OrderStep,
	Recommendation,
	ViewListItem
} from '../../../../DataLayerAnalytics/types';
import { Currency } from '../../../../enums';
import getOrderDataLayer from '../../../../DataLayerAnalytics/orderDataLayerInstance';
import { OrderType } from '../../../../DataLayerAnalytics/OrderDataLayer/types';
import SearchResultsDataLayer from '../../../../DataLayerAnalytics/OrderDataLayer/SearchResultsDataLayer';
import { SimpleFlight } from '../../../types';
import { getMinPrices } from '../../../utils';
import { SelectedFare } from '../../../../DesktopFlight/types';

export const setDataLayerParams = (currency: Currency, promotionCode: string = null) => {
	(getOrderDataLayer(OrderType.SearchResults) as SearchResultsDataLayer).init(currency, promotionCode);
};

const getTravellersTypeCount = (searchParameters: AviaSearchParameters) => {
	try {
		return searchParameters.passengers.map(passenger => `${passenger.count}${passenger.passengerType}`);
	} catch (e) {
		return [];
	}
};

const getFlightMinPrices = (flight: ResultsLegFragment, selectedFares: SelectedFare[]) => {
	try {
		return getMinPrices(flight.pricesForFareGroups, true, selectedFares, false);
	} catch (e) {
		return [];
	}
};

const getFlightInfo = (flight: ResultsLegFragment, searchParameters: AviaSearchParameters): ViewListItem => {
	return {
		item_id: `${flight.segments[0].segment.marketingAirline.iata}-${flight.segments[0].segment.flightNumber}`,
		item_brand: `${flight.segments.map(segment => segment.segment.departure.airport.iata).join('-')}-${
			flight.segments[flight.segments.length - 1].segment.arrival.airport.iata
		}`,
		item_category4: flight.segments[0].segment.departure.date,
		item_category: ItemCategory.Flight,
		item_category2: OrderStep.Booking,
		item_category5: getTravellersTypeCount(searchParameters)
	};
};

export const analytics_setFareGroupsListItems = (
	fareGroup: FareFamilyPriceFragment[],
	flight: ResultsLegFragment,
	searchParameters: AviaSearchParameters
) => {
	const items = fareGroup
		.filter(fare => !!fare.prices?.length)
		.map((fare, key) => {
			return {
				...getFlightInfo(flight, searchParameters),
				item_name: fare.fareFamily?.title,
				price: fare.prices[0]?.price?.amount,
				item_category3: fare.fareFamily?.isRecommended
					? Recommendation.Recommended
					: Recommendation.NoRecommended,
				quantity: 1,
				index: key + 1
			} as ViewListItem;
		});

	const dataLayer = getOrderDataLayer() as SearchResultsDataLayer;

	if (dataLayer.getOrderType() === OrderType.SearchResults) {
		dataLayer.getDataLayer().pushToDataLayer(DataLayerEvent.ViewItemList, items, {}, DataLayerItemsListName.Fares);
	}
};

export const analytics_setFlightsListItems = (
	flights: ResultsLegFragment[],
	selectedFares: SelectedFare[],
	searchParameters: AviaSearchParameters
) => {
	const items = flights.map((flight, key) => {
		const minPrices = getFlightMinPrices(flight, selectedFares);

		return {
			...getFlightInfo(flight, searchParameters),
			item_name: minPrices[0]?.fareFamily.title ?? '',
			item_category3: 'no recommendation',
			quantity: 1,
			price: minPrices[0]?.price.amount ?? 0,
			index: key + 1
		} as ViewListItem;
	});

	const dataLayer = getOrderDataLayer() as SearchResultsDataLayer;

	if (dataLayer.getOrderType() === OrderType.SearchResults && items.length) {
		dataLayer
			.getDataLayer()
			.pushToDataLayer(DataLayerEvent.ViewItemList, items, {}, DataLayerItemsListName.SearchResults);
	}
};

export const analytics_fareAddToCart = (
	fare: FareFamilyPriceFragment,
	index: number,
	flight: ResultsLegFragment,
	searchParameters: AviaSearchParameters
) => {
	const item: ViewListItem = {
		...(getFlightInfo(flight, searchParameters) as ViewListItem),
		item_name: fare.fareFamily?.title,
		price: fare.prices?.[0]?.price?.amount,
		item_category3: fare.fareFamily?.isRecommended ? Recommendation.Recommended : Recommendation.NoRecommended,
		quantity: 1,
		index
	};

	const dataLayer = getOrderDataLayer() as SearchResultsDataLayer;

	if (dataLayer.getOrderType() === OrderType.SearchResults) {
		dataLayer.getDataLayer().pushToDataLayer(DataLayerEvent.AddToCart, [item], {}, DataLayerItemsListName.Fares);
	}
};

export const analytics_fareRemoveFromCart = (
	fareName: string,
	farePrice: number,
	legId: number,
	isRecommended: boolean,
	flight: SimpleFlight,
	searchParameters: AviaSearchParameters
) => {
	const item: ViewListItem = {
		...getFlightInfo(flight, searchParameters),
		item_name: fareName,
		price: farePrice,
		item_category3: isRecommended ? Recommendation.Recommended : Recommendation.NoRecommended,
		quantity: 1,
		index: legId
	};

	const dataLayer = getOrderDataLayer(OrderType.SearchResults) as SearchResultsDataLayer;

	dataLayer.getDataLayer().pushToDataLayer(DataLayerEvent.RemoveFromCart, [item], {}, DataLayerItemsListName.Fares);
};
